import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import hollowInfo from 'assets/images/hollowInfo.svg'
import ArrowRight from 'components/Icons/ArrowRight'
import palette from 'constants/palette'
import { useRbacNavDisabled } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React from 'react'
import { Link } from 'react-router-dom'
import type { CaslSubject } from 'utilities/permissionMap'

const t = buildTranslate('insights_lab_v2.picker.campaign_bridge')

// The same subject that guards /thanx_campaigns in the Campaigns dropdown
// (see Navbar/Campaigns/Content.tsx).
const ALL_CAMPAIGNS_SUBJECT: CaslSubject = 'AllCampaigns'
const ALL_CAMPAIGNS_PATH = '/thanx_campaigns'

const INFO_ICON_SIZE = 18
// Off the 8px scale: `p-m` (24px) would make this card taller than the report
// rows below it. Horizontal padding stays at 24px so the icon lines up with
// the report titles.
const VERTICAL_PADDING = '14px'

// A pointer above the Campaigns & rewards report list to the per-campaign
// reporting on the All campaigns page. Mounted from ReportBrowser, which is
// shared by the picker modal and the inline browser. Owns its own bottom
// margin so the null return below leaves no empty spacer behind.
const CampaignBridgeBanner = (): JSX.Element | null => {
  const [css] = useStyletron()
  const allCampaignsDisabled = useRbacNavDisabled(ALL_CAMPAIGNS_SUBJECT)

  // Hidden rather than disabled — it points outside this catalog, so a
  // disabled row would just be noise above the list.
  if (allCampaignsDisabled) return null

  return (
    <div
      className={`d-flex align-items-center px-m mb-m ${css({
        gap: '12px',
        paddingTop: VERTICAL_PADDING,
        paddingBottom: VERTICAL_PADDING,
        backgroundColor: palette.grey10,
        borderRadius: '4px',
        // Longhand per side, not the `border` shorthand: with styletron, class
        // insertion order rather than the CSS cascade decides which wins
        // (context/knowledge/styletron-css-cascade.md).
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: palette.grey30,
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: palette.grey30,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: palette.grey30,
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        borderLeftColor: palette.grey30,
      })}`}
    >
      <img
        src={hollowInfo}
        alt=""
        aria-hidden="true"
        width={INFO_ICON_SIZE}
        height={INFO_ICON_SIZE}
      />
      <div className={css({ flex: '1 1 0', minWidth: 0 })}>
        <Text.BodyText4 bold tag="div" color="grey90">
          {t('title')}
        </Text.BodyText4>
        <Text.BodyText5 tag="div" color="grey70">
          {t('description')}
        </Text.BodyText5>
      </div>
      <Link
        to={ALL_CAMPAIGNS_PATH}
        className={`d-flex align-items-center flex-shrink-0 ${css({
          gap: '8px',
          ':focus-visible': {
            outline: `2px solid ${palette.belizeHole}`,
            outlineOffset: '2px',
          },
        })}`}
      >
        <Text.BodyText5 color="primary" tag="span">
          {t('cta')}
        </Text.BodyText5>
        <ArrowRight fillColor={palette.belizeHole} />
      </Link>
    </div>
  )
}

export default CampaignBridgeBanner
